import React, { useMemo } from 'react'
import {
  Box,
  Card,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Grid,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Formik } from 'formik'
import * as Yup from 'yup'

import DateRangePicker from 'components/common/DateRangePicker'
import SubTitleSubLayout from 'layouts/Landing/sublayouts/SubHeader'
import AsyncTextField from 'components/common/AsyncTextField'
import FileAttachDropzone from 'components/manual_order/FileAttachDropzone'
import useManualOrder from 'hooks/manual_order/useManualOrder'
import { useNavigate } from 'react-router-dom'
import useNotification from 'hooks/context/useNotification'
import useClientDataList from 'hooks/flow/useClientDataList'
import useConfiguration from 'hooks/useConfiguration'

const validationSchema = Yup.object().shape({
  clientId: Yup.string().required('Client is required'),
  orderProgramName: Yup.string().required('Order Program Name is required'),
  approvalMethod: Yup.string().required('Approval Method is required'),
  comments: Yup.string().notRequired(),
  printType: Yup.string().required('Print Type is required'),
  plex: Yup.string().required('Plex is required'),
  foldType: Yup.string().required('Fold Type is required'),
  mode: Yup.string().required('Mode is required'),
  numberClicks: Yup.number().required('Number of Clicks is required'),
  numberEnvelopes: Yup.number().required('Number of Envelopes is required'),
  outgoingSku: Yup.string().required('Outgoing Envelope SKU is required'),
  delivMethod: Yup.string().required('Mailing Method is required'),
  meterClass: Yup.string().required('Meter Class is required'),
  internalClientName: Yup.string().required('Internal Client Name is required'),
  internalOrderDescription: Yup.string().required(
    'Internal Order Description is required'
  ),
  inserts: Yup.string().notRequired(),
  mailFiles: Yup.array()
    .min(1, 'At least one mail file is required')
    .required('Mail files are required'),
})

const approvalMethods = ['Requires Client Approval', 'No Approval Required']

const printTypes = [
  'Mono',
  'Color Inkjet',
  'Color Laser',
  'Color Laser Flood',
  'Color Laser Lam Diecut',
  'MICR',
  'CD',
  'NetJet',
  'Envelope Printer',
]

const foldTypes = ['Bi-Fold', 'C-Fold', 'Z-Fold', 'Flat']

const modes = ['Bifold', 'Trifold', 'Flat', 'Box']

const ManualOrder = () => {
  const { createManualOrder } = useManualOrder()
  const { setError } = useNotification()
  const { mpxContacts, loading } = useClientDataList()
  const navigate = useNavigate()
  const {
    instanceConfigurations: {
      manualorder_envelope_skus,
      manualorder_paper_skus,
      manualorder_allowed_descriptions,
      manualorder_meter_classes,
      manualorder_mailing_methods,
      manualorder_client_ids,
      manualorder_paper_sku_preset,
    },
  } = useConfiguration()

  const filteredMpxContacts = useMemo(() => {
    if (!Array.isArray(manualorder_client_ids) || !Array.isArray(mpxContacts))
      return []

    return mpxContacts.filter((client) =>
      manualorder_client_ids?.includes(client.FlowClientID)
    )
  }, [mpxContacts, manualorder_client_ids])

  const paperSkuPresets = useMemo(() => {
    if (!manualorder_paper_sku_preset) return {}

    return Object.entries(manualorder_paper_sku_preset).reduce(
      (acc, [key, value]) => {
        // Initialize array of 6 empty paper entries
        const papers = Array.from({ length: 6 }, () => ({
          stock: undefined,
          sku_id: '',
        }))

        // Place SKUs at their correct indices based on numeric keys
        Object.entries(value).forEach(([index, sku]) => {
          const idx = parseInt(index) - 1 // Convert "1" to 0, "3" to 2, etc.
          if (idx >= 0 && idx < 6) {
            papers[idx].sku_id = sku
          }
        })

        acc[key] = papers
        return acc
      },
      {}
    )
  }, [manualorder_paper_sku_preset])

  const validate = (values) => {
    const errors = {}
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (!values.dueDate?.[0] || !(values.dueDate[0] instanceof Date)) {
      errors.dueDate = 'Desired Due Date is required'
    } else if (values.dueDate[0] <= today) {
      errors.dueDate = 'Desired Due Date must be in the future'
    }

    // Validate first paper entry
    if (values.papers?.[0]) {
      const firstPaper = values.papers[0]
      if (!firstPaper.stock) {
        errors.papers = errors.papers || []
        errors.papers[0] = errors.papers[0] || {}
        errors.papers[0].stock = 'Stock is required'
      }
      if (!firstPaper.sku_id) {
        errors.papers = errors.papers || []
        errors.papers[0] = errors.papers[0] || {}
        errors.papers[0].sku_id = 'SKU is required'
      }
    } else {
      errors.papers = 'At least one paper is required'
    }

    if (values.delivMethod?.toUpperCase() === 'CERTIFICATE OF MAILING') {
      if (!values.mailingCertificateFile) {
        errors.mailingCertificateFile =
          'Certificate of Mail Paperwork is required'
      }
    }

    return errors
  }

  const handleFormSubmit = async (values) => {
    try {
      const data = await createManualOrder(values)
      navigate(`/command-center/order-details/${data.orderid}`)
    } catch (err) {
      setError(
        err.response?.data?.display_message ||
          err?.message ||
          'Error creating order.'
      )
    }
  }

  return (
    <SubTitleSubLayout disableMargin title="Add Manual Order">
      <Card
        sx={{
          p: 4,
          marginY: 4,
          maxWidth: 600,
          border: '1px solid',
          borderColor: 'lightgray.main',
        }}
      >
        <Formik
          enableReinitialize
          initialValues={{
            clientId:
              filteredMpxContacts.length === 1
                ? filteredMpxContacts[0].FlowClientID
                : '',
            orderProgramName:
              manualorder_allowed_descriptions?.[0] || 'Chubb Adhoc Mailers',
            approvalMethod: 'No Approval Required',
            dueDate: [undefined, undefined],
            comments: '',
            printType: '',
            plex: 'Simplex',
            foldType: '',
            mode: '',
            numberClicks: '',
            numberEnvelopes: '',
            outgoingSku: '',
            delivMethod: '',
            meterClass: '',
            inserts: '',
            papers: Array.from({ length: 1 }, () => ({
              stock: '',
              sku_id: '',
            })),
            printFiles: [],
            mailFiles: [],
            internalClientName: '',
            internalOrderDescription: '',
            machineInserted: 'No',
            staticPageCount: '',
            mailingCertificateFile: undefined,
          }}
          onSubmit={handleFormSubmit}
          validationSchema={validationSchema}
          validate={validate}
        >
          {({
            handleSubmit,
            values,
            errors,
            touched,
            handleChange,
            isSubmitting,
            setFieldError,
            setFieldValue,
          }) => (
            <form noValidate onSubmit={handleSubmit}>
              <Typography
                variant="h2"
                align="left"
                fontWeight="bold"
                sx={{ mb: 6 }}
              >
                Order Details
              </Typography>
              <Stack gap={4}>
                <TextField
                  select
                  fullWidth
                  required
                  label="MPX Client Name"
                  name="clientId"
                  value={values.clientId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {filteredMpxContacts.map((client) => (
                    <MenuItem
                      key={client.FlowClientID}
                      value={client.FlowClientID}
                    >
                      {client.FlowClientName}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  required
                  label="MPX Order Description"
                  name="orderProgramName"
                  value={values.orderProgramName}
                  onChange={handleChange}
                  error={Boolean(
                    errors.orderProgramName && touched.orderProgramName
                  )}
                  helperText={
                    touched.orderProgramName ? errors.orderProgramName : ''
                  }
                >
                  {(manualorder_allowed_descriptions || []).map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </TextField>

                <AsyncTextField
                  fullWidth
                  label="Internal Client Name"
                  name="internalClientName"
                  value={values.internalClientName}
                  error={Boolean(
                    errors.internalClientName && touched.internalClientName
                  )}
                  helperText={
                    touched.internalClientName ? errors.internalClientName : ''
                  }
                  onChange={handleChange}
                  required={true}
                />

                <AsyncTextField
                  fullWidth
                  label="Internal Order Description"
                  name="internalOrderDescription"
                  value={values.internalOrderDescription}
                  error={Boolean(
                    errors.internalOrderDescription &&
                      touched.internalOrderDescription
                  )}
                  helperText={
                    touched.internalOrderDescription
                      ? errors.internalOrderDescription
                      : ''
                  }
                  onChange={handleChange}
                  required={true}
                />

                <TextField
                  select
                  fullWidth
                  required
                  label="Approval Method"
                  name="approvalMethod"
                  value={values.approvalMethod}
                  onChange={handleChange}
                  error={Boolean(
                    errors.approvalMethod && touched.approvalMethod
                  )}
                  helperText={
                    touched.approvalMethod ? errors.approvalMethod : ''
                  }
                >
                  {approvalMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </TextField>

                <DateRangePicker
                  name="dueDate"
                  label="Desired Due Date"
                  buttonConfig={[7, 8, 9, 10]}
                  single
                  error={Boolean(errors.dueDate && touched.dueDate)}
                  helperText={touched.dueDate && errors.dueDate}
                  value={values.dueDate}
                  setValue={(value) => {
                    handleChange({ target: { name: 'dueDate', value } })
                  }}
                  required
                  modalProps={{
                    isDisabled: (date) => {
                      const today = new Date()
                      today.setHours(0, 0, 0, 0)
                      return date <= today
                    },
                  }}
                />

                <Typography
                  variant="h2"
                  align="left"
                  fontWeight="bold"
                  sx={{ mt: 2, mb: 1 }}
                >
                  Print
                </Typography>

                <TextField
                  select
                  fullWidth
                  required
                  label="Print Type"
                  name="printType"
                  value={values.printType}
                  onChange={handleChange}
                  error={Boolean(errors.printType && touched.printType)}
                  helperText={touched.printType ? errors.printType : ''}
                >
                  {printTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  required
                  label="Plex"
                  name="plex"
                  value={values.plex}
                  onChange={handleChange}
                  error={Boolean(errors.plex && touched.plex)}
                  helperText={touched.plex ? errors.plex : ''}
                >
                  <MenuItem value="Simplex">Simplex</MenuItem>
                  <MenuItem value="Duplex">Duplex</MenuItem>
                </TextField>

                <TextField
                  select
                  fullWidth
                  required
                  label="Fold Type"
                  name="foldType"
                  value={values.foldType}
                  onChange={handleChange}
                  error={Boolean(errors.foldType && touched.foldType)}
                  helperText={touched.foldType ? errors.foldType : ''}
                >
                  {foldTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  required
                  label="Mode"
                  name="mode"
                  value={values.mode}
                  onChange={handleChange}
                  error={Boolean(errors.mode && touched.mode)}
                  helperText={touched.mode ? errors.mode : ''}
                >
                  {modes.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </TextField>

                <AsyncTextField
                  fullWidth
                  required
                  type="number"
                  label="Number of Clicks"
                  name="numberClicks"
                  value={values.numberClicks}
                  onChange={handleChange}
                  error={Boolean(errors.numberClicks && touched.numberClicks)}
                  helperText={touched.numberClicks ? errors.numberClicks : ''}
                  inputProps={{ min: 0 }}
                />

                <AsyncTextField
                  fullWidth
                  type="number"
                  label="Number of Envelopes"
                  name="numberEnvelopes"
                  value={values.numberEnvelopes}
                  onChange={handleChange}
                  required={true}
                  error={Boolean(
                    errors.numberEnvelopes && touched.numberEnvelopes
                  )}
                  helperText={
                    touched.numberEnvelopes ? errors.numberEnvelopes : ''
                  }
                  inputProps={{ min: 0 }}
                />

                <Typography color="primary" fontWeight="medium">
                  Paper SKUs
                </Typography>

                {Object.keys(paperSkuPresets || {}).length > 0 && (
                  <TextField
                    select
                    fullWidth
                    name="preset"
                    label="Preset"
                    value={values.preset}
                    onChange={(e) => {
                      if (paperSkuPresets[e.target.value]) {
                        setFieldValue('papers', paperSkuPresets[e.target.value])
                      } else {
                        setFieldValue(
                          'papers',
                          Array.from({ length: 6 }, () => ({
                            stock: '',
                            sku_id: '',
                          }))
                        )
                      }
                    }}
                    required={false}
                  >
                    <MenuItem value="">Select a preset...</MenuItem>
                    {Object.keys(paperSkuPresets || {}).map((key) => (
                      <MenuItem key={key} value={key}>
                        {key}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {Array.from({ length: 1 }).map((_, i) => (
                  <>
                    <Grid key={i} container spacing={2} alignItems="center">
                      <Grid item xs={5}>
                        <AsyncTextField
                          fullWidth
                          type="number"
                          label="Stock"
                          name={`papers[${i}].stock`}
                          value={values.papers[i]?.stock}
                          onChange={handleChange}
                          required={
                            i === 0 ||
                            (values.papers[i]?.sku_id &&
                              values.papers[i]?.sku_id.length > 0)
                          }
                          error={
                            touched.papers?.[i]?.stock &&
                            Boolean(errors.papers?.[i]?.stock)
                          }
                          helperText={
                            touched.papers?.[i]?.stock &&
                            errors.papers?.[i]?.stock
                          }
                          inputProps={{ min: 0 }}
                        />
                      </Grid>

                      <Grid item xs={7}>
                        <TextField
                          fullWidth
                          select
                          label="SKU"
                          name={`papers[${i}].sku_id`}
                          value={values.papers[i]?.sku_id}
                          onChange={handleChange}
                          required={i === 0}
                          error={
                            touched.papers?.[i]?.sku_id &&
                            Boolean(errors.papers?.[i]?.sku_id)
                          }
                          helperText={
                            touched.papers?.[i]?.sku_id &&
                            errors.papers?.[i]?.sku_id
                          }
                        >
                          <MenuItem value="">choose</MenuItem>
                          {(manualorder_paper_skus || []).map((sku) => (
                            <MenuItem key={sku} value={sku}>
                              {sku}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </>
                ))}

                <Typography
                  variant="h2"
                  align="left"
                  fontWeight="bold"
                  sx={{ mt: 2, mb: 1 }}
                >
                  Mail
                </Typography>

                <TextField
                  select
                  fullWidth
                  required
                  label="Will this be Machine Inserted?"
                  name="machineInserted"
                  value={values.machineInserted}
                  onChange={handleChange}
                  error={Boolean(
                    errors.machineInserted && touched.machineInserted
                  )}
                  helperText={
                    touched.machineInserted ? errors.machineInserted : ''
                  }
                >
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                </TextField>

                {values.machineInserted === 'Yes' && (
                  <AsyncTextField
                    fullWidth
                    type="number"
                    label="Static Page Count per Package"
                    name="staticPageCount"
                    value={values.staticPageCount}
                    onChange={handleChange}
                    required={false}
                    helperText="Enter the static page count per package."
                  />
                )}

                <FileAttachDropzone
                  name="mailFiles"
                  display="Mail Files"
                  setFieldError={setFieldError}
                  error={errors.mailFiles}
                  files={values.mailFiles}
                  onChange={(files) => {
                    handleChange({
                      target: { name: 'mailFiles', value: files },
                    })
                  }}
                  onRemove={(index) => {
                    const newFiles = [...values.mailFiles]
                    newFiles.splice(index, 1)
                    handleChange({
                      target: { name: 'mailFiles', value: newFiles },
                    })
                  }}
                />

                <TextField
                  select
                  fullWidth
                  required
                  label="Outgoing Envelope SKU"
                  name="outgoingSku"
                  value={values.outgoingSku}
                  onChange={handleChange}
                  error={Boolean(errors.outgoingSku && touched.outgoingSku)}
                  helperText={touched.outgoingSku ? errors.outgoingSku : ''}
                >
                  <MenuItem value="">choose</MenuItem>
                  {(manualorder_envelope_skus || []).map((sku) => (
                    <MenuItem key={sku} value={sku}>
                      {sku}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  required
                  label="Mailing Method"
                  name="delivMethod"
                  value={values.delivMethod}
                  onChange={handleChange}
                  error={Boolean(errors.delivMethod && touched.delivMethod)}
                  helperText={touched.delivMethod ? errors.delivMethod : ''}
                >
                  <MenuItem value="">choose</MenuItem>
                  {(manualorder_mailing_methods || []).map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </TextField>

                {values.delivMethod?.toUpperCase() ===
                  'CERTIFICATE OF MAILING' && (
                  <FileAttachDropzone
                    name="mailingCertificateFile"
                    display="Certificate of Mail Paperwork"
                    setFieldError={setFieldError}
                    error={errors.mailingCertificateFile}
                    files={
                      values.mailingCertificateFile
                        ? [values.mailingCertificateFile]
                        : []
                    }
                    onChange={(files) => {
                      handleChange({
                        target: {
                          name: 'mailingCertificateFile',
                          value: files?.[0],
                        },
                      })
                    }}
                    onRemove={() => {
                      handleChange({
                        target: {
                          name: 'mailingCertificateFile',
                          value: undefined,
                        },
                      })
                    }}
                    multiple={false}
                  />
                )}

                <TextField
                  select
                  fullWidth
                  required
                  label="Meter Class"
                  name="meterClass"
                  value={values.meterClass}
                  onChange={handleChange}
                  error={Boolean(errors.meterClass && touched.meterClass)}
                  helperText={touched.meterClass ? errors.meterClass : ''}
                >
                  <MenuItem value="">choose</MenuItem>
                  {(manualorder_meter_classes || []).map((mc) => (
                    <MenuItem key={mc} value={mc}>
                      {mc}
                    </MenuItem>
                  ))}
                </TextField>

                <AsyncTextField
                  name="inserts"
                  label="Inserts"
                  variant="filled"
                  placeholder="Describe any Inserts required for this job"
                  rows={4}
                  multiline
                  value={values.inserts}
                  onChange={handleChange}
                  required={false}
                  helperText="Describe any Inserts required for this job"
                />

                <AsyncTextField
                  fullWidth
                  label="Notes"
                  name="comments"
                  value={values.comments}
                  onChange={handleChange}
                  variant="filled"
                  multiline
                  rows={4}
                  manualorder_paper_skus
                  required={false}
                />

                <LoadingButton
                  sx={{ mt: 4 }}
                  type="submit"
                  variant="contained"
                  fullWidth
                  loading={isSubmitting}
                >
                  Save Order
                </LoadingButton>
              </Stack>
            </form>
          )}
        </Formik>
      </Card>
      <Box sx={{ height: 16 }} />
    </SubTitleSubLayout>
  )
}

export default ManualOrder
